<template>
    <div class="dashboard-configuration-field">
        <div class="mb-2 text-muted small">
            <span v-if="hiddenCardsCount > 0">
                {{ hiddenCardsCount }} card{{ hiddenCardsCount !== 1 ? 's' : '' }} hidden
            </span>
            <span v-else>
                No cards hidden
            </span>
        </div>
        <b-button variant="primary" size="sm" @click="openModal">
            <i class="fas fa-cog mr-1"></i>
            Configure
        </b-button>

        <DashboardCustomizeModal
            :modal-id="'tenantDashboardConfigModal'"
            :mode="'tenant'"
            :cards="currentCards"
            :tenant-hidden-cards="currentHiddenCards"
            @save-settings="onSaveSettings"
        />
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import DashboardCustomizeModal, { DashboardCustomizeSaveEvent } from "@/components/Patient/DashboardCustomizeModal.vue";
import { DashboardCardConfig, DEFAULT_DASHBOARD_CARDS } from "@/models/DashboardSettings";

export interface DashboardConfigurationValue {
    defaultLayout: string | null;
    hiddenCards: string[] | null;
}

@Component({
    name: "DashboardConfigurationField",
    components: {
        DashboardCustomizeModal
    },
})
export default class DashboardConfigurationField extends Vue {
    @Prop() value!: DashboardConfigurationValue;

    get currentCards(): DashboardCardConfig[] {
        if (this.value?.defaultLayout) {
            try {
                const parsed = JSON.parse(this.value.defaultLayout);
                if (parsed.cards && Array.isArray(parsed.cards)) {
                    return parsed.cards;
                }
            } catch (e) {
                console.error('Failed to parse dashboard default layout:', e);
            }
        }
        return structuredClone(DEFAULT_DASHBOARD_CARDS);
    }

    get currentHiddenCards(): string[] {
        return this.value?.hiddenCards || [];
    }

    get hiddenCardsCount(): number {
        return this.currentHiddenCards.length;
    }

    get hasCustomOrder(): boolean {
        if (!this.value?.defaultLayout) {
            return false;
        }
        try {
            const parsed = JSON.parse(this.value.defaultLayout);
            if (!parsed.cards || !Array.isArray(parsed.cards)) {
                return false;
            }
            const currentCards = parsed.cards as DashboardCardConfig[];
            for (let i = 0; i < currentCards.length; i++) {
                const current = currentCards[i];
                const defaultCard = DEFAULT_DASHBOARD_CARDS.find(d => d.id === current.id);
                if (!defaultCard) continue;
                if (current.column !== defaultCard.column ||
                    current.order !== defaultCard.order ||
                    current.visible !== defaultCard.visible) {
                    return true;
                }
            }
            return false;
        } catch (e) {
            return false;
        }
    }

    get hasTenantConfig(): boolean {
        return !!(this.value?.defaultLayout || (this.value?.hiddenCards && this.value.hiddenCards.length > 0));
    }

    openModal() {
        this.$bvModal.show('tenantDashboardConfigModal');
    }

    onSaveSettings(event: DashboardCustomizeSaveEvent) {
        const newValue: DashboardConfigurationValue = {
            defaultLayout: JSON.stringify({ cards: event.cards }),
            hiddenCards: event.hiddenCards
        };
        this.$emit('input', newValue);
    }
}
</script>

<style scoped>
.dashboard-configuration-field {
    width: 100%;
}
</style>
